import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { IMAGES } from "../lib/site";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
    <span className="mask-line">
        <motion.span
            initial={{ y: "115%" }}
            animate={{ y: "0%" }}
            transition={{ duration: 1.1, delay, ease: EASE }}
        >
            {children}
        </motion.span>
    </span>
);

const STATS = [
    ["25+", "Years of Experience"],
    ["MOQ 100", "Units & Mixed Kits"],
    ["3", "Continents Served"],
    ["OEM", "Private Label Ready"],
];

export const Hero = ({ onNavigate }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
    const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden grain">
            <motion.div className="absolute inset-0" style={{ y: bgY, scale: bgScale }}>
                <img
                    src={IMAGES.hero}
                    alt="Handcrafted leather laptop bag by FnB International"
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F0C08] via-[#0F0C08]/55 to-[#0F0C08]/35" />
            <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-[#0F0C08]/80 to-transparent" />
            <div
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(60% 50% at 78% 18%, rgba(197,139,73,0.22) 0%, rgba(197,139,73,0) 70%)",
                }}
            />

            <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pt-40 pb-14">
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.15, ease: EASE }}
                    className="font-mono2 text-[11px] sm:text-xs tracking-[0.3em] text-[#C58B49] uppercase mb-7"
                    data-testid="hero-eyebrow"
                >
                    Manufacturer &amp; Exporter · Kanpur, India
                </motion.p>

                <h1 className="font-display font-semibold text-[#F4EFEA] text-[13.5vw] sm:text-7xl lg:text-[6.2rem] leading-[0.98] tracking-tight" data-testid="hero-headline">
                    <MaskedLine delay={0.3}>Fine leather craft,</MaskedLine>
                    <MaskedLine delay={0.45}>
                        hand-finished <em className="italic font-medium text-[#D4A373]">&amp;</em>
                    </MaskedLine>
                    <MaskedLine delay={0.6}>exported worldwide.</MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                    className="mt-7 max-w-xl text-base sm:text-lg text-[#C2B7AD] leading-relaxed"
                    data-testid="hero-subcopy"
                >
                    FnB International manufactures leather laptop bags, sling bags, wallets,
                    handmade candles and fridge magnets — made to order for buyers across the
                    Americas, Europe and Australia.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
                    className="mt-9 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-explore-btn"
                        onClick={() => onNavigate("collections")}
                        className="group px-7 py-3.5 bg-[#C58B49] text-[#0F0C08] text-[13px] font-medium tracking-[0.16em] uppercase rounded-sm hover:bg-[#D4A373] transition-colors duration-300 flex items-center gap-2"
                    >
                        Explore Collections
                        <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                    <button
                        data-testid="hero-sample-btn"
                        onClick={() => onNavigate("enquiry")}
                        className="px-7 py-3.5 border border-[#4A3E34] text-[#E2D4C9] text-[13px] tracking-[0.16em] uppercase rounded-sm hover:border-[#C58B49] hover:text-[#D4A373] transition-colors duration-300"
                    >
                        Request a Sample Kit
                    </button>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.25 }}
                    className="mt-14 pt-8 border-t border-[#F4EFEA]/10 grid grid-cols-2 md:grid-cols-4 gap-6"
                    data-testid="hero-stats"
                >
                    {STATS.map(([v, l]) => (
                        <div key={l}>
                            <div className="font-display text-3xl sm:text-4xl text-[#D4A373]">{v}</div>
                            <div className="mt-1 text-xs tracking-[0.14em] uppercase text-[#8C8075]">{l}</div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>

            <motion.button
                data-testid="hero-scroll-cue"
                onClick={() => onNavigate("collections")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute right-6 bottom-8 z-10 hidden md:flex flex-col items-center gap-2 text-[#8C8075] hover:text-[#D4A373] transition-colors"
                aria-label="Scroll to collections"
            >
                <span className="font-mono2 text-[10px] tracking-[0.3em] uppercase [writing-mode:vertical-rl]">Scroll</span>
                <motion.span animate={{ y: [0, 7, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
                    <ArrowDown size={16} />
                </motion.span>
            </motion.button>
        </section>
    );
};
